import { ApiLocation, ApiProject, ApiProjectType } from "@incendium/api";
import {
  Box,
  CircularProgress,
  Portal,
  styled,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import { PageDataTab } from "Components/PageDataTab";
import { ConversionTab } from "features/conversions/components";
import { FormHandlersTab } from "Components/FormHandlersTab";
import { TabButton } from "Components/UI/TabButton";
import { TabContainer } from "Components/UI/TabContainer";
import { useCallback, useState } from "react";
import { useUpdateEffect } from "react-use";
import {
  SidebarContainer,
  SidebarContentWrapper,
  SiderBarHeader,
} from "Components/UI/Sidebar";
import {
  ContactPage,
  DataObject,
  Edit,
  FindInPage,
  Mouse,
} from "@mui/icons-material";
import EditLocationURLDialog from "Components/Location/EditLocationURLDialog";
import { useUrlQuery } from "Hooks/useUrlQuery";
import { motion } from "motion/react";
import { useHasProjectType } from "features/project";
import { PageMetaTab } from "features/pageMeta";
import { IFrameContext } from "features/smartIFrame/providers/OldIframeProvider";
import { SmartIFrameProvider } from "features/smartIFrame/providers/SmartIFrameProvider";
import { SmartIframe } from "features/smartIFrame/components/SmartIframe";
import { smartIframeDebug } from "features/smartIFrame/utils/smartIframeDebug";

const MainWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  height: `calc(100% - 80px)`,
  marginTop: 40,
}));
const IFrameWrapper = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  height: "100%",
  margin: "auto",
}));

const ContentContainer = styled(Box)({
  height: `calc(100% - 55px)`,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
});

export const StyledButton = styled(Button)(({ theme }) => ({
  "&:not(:last-child)": {
    marginRight: theme.spacing(2),
  },
}));

export const TextFieldHeading = styled(Typography)(({ theme }) => ({
  lineHeight: 1,
  fontSize: theme.spacing(2.2),
}));

export type LocationTabs =
  | "Page Tab"
  | "Conversion Tab"
  | "Form Tab"
  | "Page Meta Tab";

export const TagPages = ({
  location,
  setLocation,
  project,
}: {
  location: ApiLocation;
  setLocation: (d: ApiLocation) => void;
  project: ApiProject;
}) => {
  const [selectedSelector, setSelected] = useState("");
  const [targetText, setTargetText] = useState("");
  const clearIframeSelection = useCallback(() => {
    smartIframeDebug("TagPages", "clearIframeSelection");
    setSelected("");
    setTargetText("");
  }, []);
  const [active] = useState(true);
  const [repeated] = useState(false);
  const [useTextMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState("100%");
  // Sidebar forms own debugTags; TagPages does not pass targets into SmartIframe.
  const [targets] = useState<string[] | undefined>(undefined);
  const [url, setUrl] = useState<string | undefined>(location.urlMatch);
  const query = useUrlQuery();
  const [selectedTab, setSelectedTab] = useState<LocationTabs>(
    query.has("tab") && query.get("tab")
      ? (query.get("tab") as LocationTabs)
      : "Page Tab",
  );
  const hasProjectType = useHasProjectType();

  useUpdateEffect(() => {
    setWidth(isMobile ? "420px" : "100%");
  }, [isMobile]);

  useUpdateEffect(() => {
    smartIframeDebug("TagPages", "selection changed", {
      selectedSelector,
      targetText,
      selectedTab,
      propTargets: targets,
    });
  }, [selectedSelector, targetText, selectedTab, targets]);

  useUpdateEffect(() => {
    smartIframeDebug("TagPages", "tab changed (sidebar forms remount)", {
      selectedTab,
    });
  }, [selectedTab]);

  return (
    <SmartIFrameProvider>
      <IFrameContext.Provider
        value={{
          selectedSelector,
          selectedText: targetText,
          clearIframeSelection,
        }}
      >
        <Portal container={() => document.getElementById("pageAction")}>
          <Stack alignItems={"flex-end"}>
            <FormControlLabel
              style={{ minWidth: 136 }}
              control={
                <Switch
                  checked={isMobile}
                  onChange={(e, c) => setIsMobile(c)}
                />
              }
              label="mobile view"
            />
            <Stack
              spacing={1}
              direction="row"
              alignItems={"center"}
              sx={{ maxWidth: 520 }}
            >
              <Typography fontWeight={"bold"}>viewing:</Typography>
              <Tooltip arrow title={url}>
                <Typography noWrap>{url}</Typography>
              </Tooltip>
              <IconButton size="small" onClick={() => setOpen(true)}>
                <Edit fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Portal>
        {url ? (
          <MainWrapper>
            <IFrameWrapper
              sx={{
                width,
              }}
            >
              <SmartIframe
                src={url}
                active={active}
                targets={targets}
                selector={selectedSelector}
                targetText={targetText}
                repeated={repeated}
                useTextMode={useTextMode}
                onClick={(e) => {
                  smartIframeDebug("TagPages", "iframe onClick → selection", {
                    selector: e.selector,
                    text: e.text,
                    tab: selectedTab,
                    propTargets: targets,
                  });
                  if (e.text && e.text !== "") {
                    setTargetText(e.text);
                  }
                  setSelected(e.selector);
                }}
              />
            </IFrameWrapper>
          </MainWrapper>
        ) : (
          <CircularProgress />
        )}
        <Portal container={() => document.getElementById("rightSideBar")}>
          <SidebarContainer>
            <SiderBarHeader>
              <Typography variant="h3" gutterBottom>
                {selectedTab === "Page Tab" && "tag this page"}
                {selectedTab === "Conversion Tab" && "conversions"}
                {selectedTab === "Page Meta Tab" && "Page Meta"}
                {selectedTab === "Form Tab" && "form handlers"}
              </Typography>
              <Typography variant="body1">
                {selectedTab === "Page Tab" && (
                  <>train incendium to recognise page items</>
                )}
                {selectedTab === "Conversion Tab" &&
                  "train incendium to recognise conversions"}
                {selectedTab === "Page Meta Tab" &&
                  "train incendium to capture on visit page meta data, such as if a product is in stock etc"}
                {selectedTab === "Form Tab" &&
                  "train incendium to recognise populate contact info from form fields"}
              </Typography>
            </SiderBarHeader>
            <Divider />

            <SidebarContentWrapper>
              <Box px={3}>
                <TabContainer fullWidth noGrow>
                  <TabButton
                    onClick={() => setSelectedTab("Page Tab")}
                    isActive={selectedTab === "Page Tab"}
                  >
                    <Tooltip
                      arrow
                      title={
                        <>
                          <Typography variant="subtitle2" color={"inherit"}>
                            Page Data
                          </Typography>
                          <Typography variant="body2" color={"inherit"}>
                            train incendium to recognise page items
                          </Typography>
                        </>
                      }
                    >
                      <FindInPage />
                    </Tooltip>
                  </TabButton>
                  <TabButton
                    onClick={() => setSelectedTab("Conversion Tab")}
                    isActive={selectedTab === "Conversion Tab"}
                  >
                    <Tooltip
                      arrow
                      title={
                        <>
                          <Typography variant="subtitle2" color={"inherit"}>
                            Conversions
                          </Typography>
                          <Typography variant="body2" color={"inherit"}>
                            train incendium to recognise conversions
                          </Typography>
                        </>
                      }
                    >
                      <Mouse />
                    </Tooltip>
                  </TabButton>

                  {/* currently only meta is the out of stock, so we only show option if ecom */}
                  {hasProjectType(ApiProjectType.TYPE_ECOMMERCE) && (
                    <TabButton
                      onClick={() => setSelectedTab("Page Meta Tab")}
                      isActive={selectedTab === "Page Meta Tab"}
                    >
                      <Tooltip
                        arrow
                        title={
                          <>
                            <Typography variant="subtitle2" color={"inherit"}>
                              Page Meta
                            </Typography>
                            <Typography variant="body2" color={"inherit"}>
                              train incendium to capture on visit page meta
                              data, such as if a product is in stock etc
                            </Typography>
                          </>
                        }
                      >
                        <DataObject />
                      </Tooltip>
                    </TabButton>
                  )}
                  <TabButton
                    onClick={() => setSelectedTab("Form Tab")}
                    isActive={selectedTab === "Form Tab"}
                  >
                    <Tooltip
                      arrow
                      title={
                        <>
                          <Typography variant="subtitle2" color={"inherit"}>
                            Contacts
                          </Typography>
                          <Typography variant="body2" color={"inherit"}>
                            train incendium to recognise populate contact info
                            from form fields
                          </Typography>
                        </>
                      }
                    >
                      <ContactPage />
                    </Tooltip>
                  </TabButton>
                </TabContainer>
              </Box>

              <ContentContainer>
                <Box
                  key={selectedTab}
                  component={motion.div}
                  initial={{
                    opacity: 0,
                    x: 50,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {selectedTab === "Page Tab" && (
                    <PageDataTab
                      location={location}
                      setLocation={setLocation}
                      project={project}
                      active={active}
                    />
                  )}
                  {selectedTab === "Conversion Tab" && (
                    <ConversionTab
                      location={location}
                      project={project}
                      isMobile={isMobile}
                    />
                  )}
                  {selectedTab === "Page Meta Tab" && (
                    <PageMetaTab location={location} project={project} />
                  )}
                  {selectedTab === "Form Tab" && (
                    <FormHandlersTab location={location} project={project} />
                  )}
                </Box>
              </ContentContainer>
            </SidebarContentWrapper>
          </SidebarContainer>
        </Portal>
      </IFrameContext.Provider>
      <EditLocationURLDialog
        open={open}
        setOpen={setOpen}
        setUrl={setUrl}
        url={url}
        location={location}
        project={project}
      />
    </SmartIFrameProvider>
  );
};
